import { Product, ProductSlug } from "@/api/product-api";

/**
 * **사이즈 라인** — 사이즈마다 **별도 제품**인 것을 화면에서 한 제품으로 묶는다 (#5149).
 *
 * 첫 사례는 Inner Gloves for TactGlove DK3 다. 데이터상 S/M/L/XL 이 각각 `ACCESSORY` 제품이고
 * SKU 옵션이 비어 있다(`options: []`). mgmt 에서 1제품 + 사이즈 옵션으로 합치는 대신 **데이터를
 * 그대로 두고** 묶는다 — 이유는 `docs/product-variant-line-policy.md` 「사이즈 라인」:
 *
 * - DK3 PDP·카트시트·카트 추천의 「고른 글러브 사이즈에 맞는 이너글러브만」 매칭
 *   (`ProductUtil.isInnerGloveSlugForSize`)이 **slug 접미사**에 기대므로 그대로 동작한다
 * - 신·구 제품이 공존하는 이행 창(구 SKU 가 신규 제품을 가리고 청구까지 간다 — #5149 코멘트)이
 *   **생기지 않는다**
 * - 주문·재고·출고 SKU 가 바뀌지 않는다
 *
 * ## 동작
 *
 * - **목록**(`ProductUtil.getAccessories`)에는 **대표 1장**만 — 라인 이름(`name`)·사이즈 없는
 *   썸네일(`thumbnail`)로 그린다
 * - **각 사이즈 PDP 는 그대로 산다** — 리다이렉트 0. 사이즈 선택지(`SizeLineSelect`)가 형제
 *   PDP 로 이동시킨다. 사이즈별 URL 이 살아 있어야 GMC 피드·사이트맵이 홉 없이 남는다
 *
 * ⚠️ **slug 패턴(`-s`/`-m`)으로 추론하지 않는다** — `bundle-base.ts`·`set-variant.ts` 와 같은
 * 이유(`docs/product-variant-line-policy.md` §2). 새 사이즈 라인은 여기 한 항목.
 * ⚠️ 멤버 순서가 **표시 순서**이고, 대표 선정 순서다(아래 `sizeLineRepresentative`).
 */
export type SizeLine = {
  /** 목록 카드에 그릴 사이즈 무관 이름. 공식 영문 제품명이라 번역하지 않는다. */
  name: string;
  /**
   * 목록 카드에 그릴 **사이즈 표시 없는** 썸네일. 사이즈별 제품 썸네일은 이미지 안에 「S」 같은
   * 사이즈 표시가 박혀 있어서(`tactglove-inner_s.png`), 대표 사이즈 그림을 그대로 쓰면 카드가
   * 「S 제품」으로 읽힌다(2026-10-06 3002 화면 확인). PDP 는 사이즈별 페이지라 사이즈별 그림이 맞다.
   * ⚠️ 이미지를 바꿀 때는 **새 파일명**으로 — 같은 URL 덮어쓰기는 이미지 최적화 캐시에 하루 남는다.
   */
  thumbnail: string;
  /**
   * `size` = 칩·라벨 표기(S/M/…) · `option` = 사이즈 **옵션 값**(`small`/`medium`/`large`/`x-large`) —
   * 글러브 SKU 옵션과 같은 어휘다. 재입고 알림 모달의 사이즈 칸(`notify-me-options` 가 이 어휘로 사이즈를
   * 인식)에 쓴다(`sizeLineNotifyProduct`).
   */
  members: { size: string; option: string; slug: ProductSlug }[];
};

export const SIZE_LINES: SizeLine[] = [
  {
    name: "Inner Gloves for TactGlove DK3",
    // 사이즈별 그림(tactglove-inner_s.png)에서 「S」 배지만 지운 것 — 장갑 크기·위치가 사이즈별
    // 그림과 같다(2026-10-06 업로드). 종전 후보 tactglove-inner.png 는 여백이 커서 장갑이 작게 보였다.
    thumbnail:
      "https://cdn.bhaptics.com/images/products/tactglove-dk3-inner.png",
    members: [
      {
        size: "S",
        option: "small",
        slug: ProductSlug.INNER_GLOVES_FOR_TACTGLOVE_DK3_S,
      },
      {
        size: "M",
        option: "medium",
        slug: ProductSlug.INNER_GLOVES_FOR_TACTGLOVE_DK3_M,
      },
      {
        size: "L",
        option: "large",
        slug: ProductSlug.INNER_GLOVES_FOR_TACTGLOVE_DK3_L,
      },
      {
        size: "XL",
        option: "x-large",
        slug: ProductSlug.INNER_GLOVES_FOR_TACTGLOVE_DK3_XL,
      },
    ],
  },
];

/** 이 slug 가 속한 사이즈 라인. 없으면 `undefined`. */
export const sizeLineOf = (slug: string): SizeLine | undefined =>
  SIZE_LINES.find((line) => line.members.some((m) => m.slug === slug));

const inStock = (p: Product) => p.skuOptions.some((o) => (o.stock ?? 0) > 0);

/**
 * 라인의 **대표 제품** — 목록 카드가 가리키는 사이즈. 카탈로그에 있는 멤버 중 **재고 있는 첫
 * 사이즈**, 전부 품절이면 카탈로그에 있는 첫 사이즈. 카탈로그에 하나도 없으면 `undefined`.
 *
 * 재고를 먼저 보는 이유: 카드의 품절 표시·담기가 대표 제품 기준이라, 첫 사이즈(S)만 품절이어도
 * 라인 전체가 품절로 보이게 된다.
 */
export const sizeLineRepresentative = (
  products: Product[],
  line: SizeLine,
): Product | undefined => {
  const present = line.members
    .map((m) => products.find((p) => p.slug === m.slug))
    .filter((p): p is Product => !!p);
  return present.find(inStock) ?? present[0];
};

/**
 * **목록용으로 접힌 라인 카드인가** — `ProductUtil.getAccessories` 가 만든 사본(이름이 라인 이름).
 *
 * ★이 카드에서는 **바로 담지 않는다** (#5922 R1 1번). 사본의 SKU 는 대표 사이즈(재고 있는 첫
 * 사이즈 = 보통 S)의 것이라, 담기 버튼이 사용자가 고르지 않은 사이즈를 담았다 — 카드엔 사이즈가
 * 안 보이고, 사이즈는 담긴 **뒤의** 팝업에서야 보였다. 장갑 사이즈가 틀리면 반품이다.
 *
 * 판정을 「라인 멤버인가」(`sizeLineOf`)로 넓히지 않는다 — `ProductCard` 는 카트 추천·쿠폰 대상
 * 등에서 **원본 사이즈 제품**(이름 「… (S)」)으로도 그려지고, 그때는 그 사이즈를 바로 담는 게 맞다.
 */
export const isSizeLineCard = (p: Pick<Product, "slug" | "name">): boolean =>
  sizeLineOf(p.slug)?.name === p.name;

/**
 * 목록의 「Add to Bag」 이 띄울 **옵션 다이얼로그용 제품** — 카탈로그에 있는 사이즈를 행(`skuOptions`)으로 모은다.
 *
 * 같은 페이지의 다른 액세서리(색이 여러 개인 배지 등)는 「Add to Bag」 → `SkuOptionsDialog` 에서 행마다
 * 수량을 고른다. 라인 카드도 같은 방식으로 사이즈를 고르게 한다(#5922 R1 — 사이즈를 고르지 않고 대표 S 가
 * 담기던 문제).
 *
 * - **행 = 카탈로그에 있는 사이즈 전부**(품절 포함). 품절 행은 다이얼로그가 수량 대신 「Notify me」로 그린다
 *   (`SkuOptionsDialog` 의 `SkuOptionRow`) — 다른 액세서리 다이얼로그와 같은 동작이다
 * - **재고 있는 사이즈가 하나도 없으면 `null`** → 호출처가 다이얼로그 대신 카드 「Notify me」(All sizes)를 그린다
 *   — 목록 카드의 전부 품절 처리와 같다
 * - **행 이름·그림 = 각 사이즈 제품 것**(「… (S)」 · 「S」가 박힌 그림) — 무엇을 고르는지 보이게.
 *   이너글러브는 SKU 썸네일이 비어 있어(prod·dev) 제품 썸네일로 채운다
 * - ★**`slugBySku` 를 함께 돌려준다.** 다이얼로그는 기본적으로 모든 행을 `product.slug` 로 담고 알림을 거는데,
 *   사이즈마다 별도 제품이라 그대로면 M 을 담아도 slug 가 대표(S) 제품이 된다
 */
export const sizeLineDialogProduct = (
  products: Product[],
  line: SizeLine,
): { product: Product; slugBySku: Record<string, string> } | null => {
  const members = line.members
    .map((m) => products.find((p) => p.slug === m.slug))
    .filter((p): p is Product => !!p);
  if (!members.some(inStock)) return null;

  const slugBySku: Record<string, string> = {};
  const skuOptions = members.map((p) => {
    const option = p.skuOptions[0];
    slugBySku[option.sku] = p.slug;
    return {
      ...option,
      name: p.name,
      thumbnail: option.thumbnail || p.thumbnail,
    };
  });
  return {
    product: { ...members[0], name: line.name, skuOptions },
    slugBySku,
  };
};

/**
 * 라인 카드의 「Notify me」 가 쓸 **알림용 제품** — 전부 품절일 때 다른 카드와 같은 재입고 알림 모달을 띄우되,
 * 모달 안에서 **사이즈를 고르게** 한다(#5922).
 *
 * 다른 액세서리 카드는 전부 품절이면 「Notify me」 → 이메일 모달이고, 옵션이 여러 개면 모달 안에서 고른다
 * (`NotifyMe` · `getNotifyMeOptions`). 이너글러브는 사이즈마다 **별도 제품**이라 그대로 두면 사이즈 칸이
 * 안 생기고 대표(S) 제품으로만 알림이 걸린다. 그래서:
 *
 * - 카탈로그에 있는 멤버 전부를 **한 제품의 SKU 옵션**으로 모으고 각 SKU 에 사이즈 옵션 값(`option`)을
 *   싣는다 → 모달이 품절 사이즈를 칸으로 그린다
 * - `slugBySku` 를 함께 돌려준다 → 신청이 고른 사이즈 제품 slug 로 간다(`resolveNotificationTargets`)
 *
 * 멤버가 하나도 없으면 `null`.
 */
export const sizeLineNotifyProduct = (
  products: Product[],
  line: SizeLine,
): { product: Product; slugBySku: Record<string, string> } | null => {
  const members = line.members
    .map((m) => ({ m, p: products.find((p) => p.slug === m.slug) }))
    .filter(
      (x): x is { m: (typeof line.members)[number]; p: Product } => !!x.p,
    );
  if (members.length === 0) return null;

  const slugBySku: Record<string, string> = {};
  const skuOptions = members.map(({ m, p }) => {
    const option = p.skuOptions[0];
    slugBySku[option.sku] = p.slug;
    return { ...option, options: [m.option] };
  });
  return {
    product: { ...members[0].p, name: line.name, skuOptions },
    slugBySku,
  };
};
